import { logAudit } from './audit';

const CLICK_SELECTOR = [
    'button',
    'a',
    '[role="button"]',
    'summary',
    '[aria-expanded]',
    '[data-audit-click]'
].join(',');

const FIELD_SELECTOR = 'input, textarea, select';
const IGNORE_SELECTOR = '[data-audit-ignore="true"], [data-audit-ignore="true"] *';
const MENU_HINTS = ['dashboard', 'trade', 'market', 'wallet', 'broker', 'mail', 'setting', 'support', 'chat', 'nav'];
const VALUE_LIMIT = 2000;

const normalizeText = (value = '') => String(value).replace(/\s+/g, ' ').trim();

const truncateValue = (value) => {
    const normalized = typeof value === 'string' ? value : String(value ?? '');
    return normalized.length > VALUE_LIMIT ? `${normalized.slice(0, VALUE_LIMIT)}...` : normalized;
};

const getElementText = (element) => {
    if (!element) return '';
    return normalizeText(
        element.getAttribute('aria-label') ||
        element.getAttribute('title') ||
        element.innerText ||
        element.textContent ||
        element.value ||
        ''
    );
};

const getClassName = (element) => {
    if (!element) return '';
    if (typeof element.className === 'string') return element.className;
    return element.getAttribute?.('class') || '';
};

const getSectionFromElement = (element) => {
    const path = [];
    let current = element;

    while (current && current !== document.body && path.length < 8) {
        const className = getClassName(current).toLowerCase();
        const id = (current.id || '').toLowerCase();
        const text = `${className} ${id}`;

        if (text.includes('navbar') || text.includes('nav')) return 'Меню';
        if (text.includes('wallet')) return 'Кошелек';
        if (text.includes('trading') || text.includes('trade')) return 'Трейдинг';
        if (text.includes('mailbox') || text.includes('mail')) return 'Почта';
        if (text.includes('settingspage')) return 'Настройки сотрудников';
        if (text.includes('sitesettings')) return 'Настройки сайта';
        if (text.includes('settings')) return 'Настройки';
        if (text.includes('broker')) return 'Брокерская программа';
        if (text.includes('dashboard') || text.includes('dashbroad')) return 'Дашборд';
        if (text.includes('chat')) return 'Чат поддержки';
        if (text.includes('header')) return 'Хедер';
        if (text.includes('modal')) return 'Модал';

        path.push(current.tagName?.toLowerCase());
        current = current.parentElement;
    }

    return 'Сайт';
};

const getFieldLabel = (field) => {
    if (!field) return 'Поле';

    if (field.id) {
        const explicitLabel = document.querySelector(`label[for="${CSS.escape(field.id)}"]`);
        const labelText = getElementText(explicitLabel);
        if (labelText) return labelText;
    }

    const parentLabel = field.closest('label');
    const parentLabelText = getElementText(parentLabel);
    if (parentLabelText) return parentLabelText;

    const container = field.closest('[class*="field"], [class*="input"], [class*="form"], [class*="setting"], [class*="modal"]');
    const nearbyLabel = container?.querySelector('label, [class*="label"], [class*="Label"]');
    const nearbyText = getElementText(nearbyLabel);
    if (nearbyText) return nearbyText;

    return field.getAttribute('aria-label') ||
        field.getAttribute('placeholder') ||
        field.name ||
        field.id ||
        'Поле';
};

const getFieldValue = (field) => {
    if (!field) return '';

    if (field.type === 'file') {
        return Array.from(field.files || []).map(file => file.name);
    }

    if (field.type === 'checkbox' || field.type === 'radio') {
        return field.checked;
    }

    return truncateValue(field.value);
};

const isNavigationClick = (target, buttonText) => {
    const section = getSectionFromElement(target);
    if (section === 'Меню') return true;

    const classChain = [];
    let current = target;
    while (current && current !== document.body && classChain.length < 5) {
        classChain.push(getClassName(current).toLowerCase());
        current = current.parentElement;
    }

    const haystack = `${classChain.join(' ')} ${buttonText}`.toLowerCase();
    return MENU_HINTS.some(hint => haystack.includes(hint));
};

const isDropdownToggle = (target) => {
    const expanded = target.getAttribute?.('aria-expanded');
    if (expanded !== null && expanded !== undefined) return true;

    const text = `${getClassName(target)} ${getElementText(target)}`.toLowerCase();
    return text.includes('dropdown') || text.includes('collapse') || text.includes('развер') || text.includes('сверн');
};

const buildTargetDetails = (target) => ({
    tag: target.tagName?.toLowerCase() || '',
    type: target.getAttribute?.('type') || '',
    className: getClassName(target),
    id: target.id || '',
    path: window.location.pathname,
    section: getSectionFromElement(target)
});

export const initGlobalInteractionAudit = () => {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
        return () => {};
    }

    const lastFieldValues = new WeakMap();
    const lastClickBySignature = new Map();

    const shouldIgnore = (target) => {
        return !target ||
            target.closest?.(IGNORE_SELECTOR) ||
            target.closest?.('[data-no-audit="true"]');
    };

    const auditClick = (event) => {
        const target = event.target?.closest?.(CLICK_SELECTOR);
        if (!target || shouldIgnore(target)) return;

        const buttonText = getElementText(target) || 'Без текста';
        const section = getSectionFromElement(target);
        const action = isNavigationClick(target, buttonText)
            ? 'menu_click'
            : isDropdownToggle(target)
                ? 'dropdown_toggle'
                : 'button_click';
        const signature = `${action}:${section}:${buttonText}`;
        const now = Date.now();

        if (now - (lastClickBySignature.get(signature) || 0) < 600) return;
        lastClickBySignature.set(signature, now);

        logAudit('site_interaction', action, buttonText, {
            ...buildTargetDetails(target),
            buttonText,
            disabled: Boolean(target.disabled || target.getAttribute('aria-disabled') === 'true'),
            href: target.getAttribute?.('href') || '',
            expanded: target.getAttribute?.('aria-expanded') || null
        });
    };

    const auditField = (event) => {
        const field = event.target;
        if (!field?.matches?.(FIELD_SELECTOR) || shouldIgnore(field)) return;

        const value = getFieldValue(field);
        const serializedValue = Array.isArray(value) ? value.join('|') : String(value);
        if (lastFieldValues.get(field) === serializedValue && event.type !== 'change') return;
        lastFieldValues.set(field, serializedValue);

        const fieldLabel = getFieldLabel(field);
        const action = field.type === 'file'
            ? 'file_select'
            : event.type === 'change' && ['checkbox', 'radio', 'select-one', 'select-multiple'].includes(field.type)
                ? 'field_change'
                : 'field_blur';

        logAudit('site_interaction', action, fieldLabel, {
            ...buildTargetDetails(field),
            fieldLabel,
            name: field.name || '',
            placeholder: field.getAttribute('placeholder') || '',
            value,
            files: field.type === 'file' ? value : undefined
        });
    };

    document.addEventListener('click', auditClick, true);
    document.addEventListener('blur', auditField, true);
    document.addEventListener('change', auditField, true);

    return () => {
        document.removeEventListener('click', auditClick, true);
        document.removeEventListener('blur', auditField, true);
        document.removeEventListener('change', auditField, true);
    };
};
